// The Sano Godam icon (Figma "Sano Godam — App icon concepts", A.4 "Inventory +
// billing"): a warehouse whose open bay holds a stock parcel, beside a receipt.
// One source for the in-app logo (<BrandMark>) and every icon file
// (apps/site/scripts/icons.ts, email-icons.ts). Plain TS with no imports, so
// Node can load it directly.

export const BRAND_INDIGO = '#4f46e5'

export type IconVariant = 'master' | 'small'

/** One SVG element of the drawing: its tag and attributes (SVG names, kebab-case). */
export type IconShape = ['path' | 'rect', Record<string, string | number>]

const W = '#fff'
const I = BRAND_INDIGO
const line = (d: string, width: number): IconShape => [
  'path',
  { d, stroke: I, 'stroke-width': width, 'stroke-linecap': 'round' },
]

/**
 * Drawn on a 256 × 256 canvas, painted in order over the indigo tile, as in the
 * Figma frame "Final · A.4": "master" for 48 px and up (lines 8–10 wide, edges on
 * multiples of 4 so they stay sharp on a splash screen); "small" for 16–32 px
 * (bigger building, one shutter rail, no parcel tape, 12 px lines).
 */
export const ICON_SHAPES: Record<IconVariant, IconShape[]> = {
  master: [
    ['path', { d: 'M40 120L128 60L216 120V208H40V120Z', fill: W }],
    ['rect', { x: 76, y: 128, width: 104, height: 80, rx: 8, fill: I }],
    ['rect', { x: 88, y: 140, width: 56, height: 8, rx: 4, fill: W }],
    ['rect', { x: 88, y: 156, width: 56, height: 8, rx: 4, fill: W }],
    ['rect', { x: 88, y: 172, width: 48, height: 36, rx: 4, fill: W }],
    ['rect', { x: 108, y: 172, width: 8, height: 14, fill: I }],
    [
      'path',
      {
        d: 'M160 136H208C212.4 136 216 139.6 216 144V224L200 212L184 224L168 212L152 224V144C152 139.6 155.6 136 160 136Z',
        fill: W,
        stroke: I,
        'stroke-width': 8,
        'stroke-linejoin': 'round',
      },
    ],
    line('M168 158H200', 10),
    line('M168 178H192', 10),
    line('M184 198H200', 10),
  ],
  small: [
    ['path', { d: 'M32 116L128 52L224 116V212H32V116Z', fill: W }],
    ['rect', { x: 64, y: 124, width: 104, height: 88, rx: 8, fill: I }],
    ['rect', { x: 80, y: 140, width: 56, height: 12, rx: 6, fill: W }],
    ['rect', { x: 80, y: 164, width: 56, height: 48, rx: 4, fill: W }],
    [
      'path',
      {
        d: 'M160 132H216C220.4 132 224 135.6 224 140V228L206 216L188 228L170 216L152 228V140C152 135.6 155.6 132 160 132Z',
        fill: W,
        stroke: I,
        'stroke-width': 12,
        'stroke-linejoin': 'round',
      },
    ],
    line('M170 160H206', 12),
    line('M182 190H206', 12),
  ],
}

/**
 * The icon as an SVG document. `shape`: "rounded" (app tile), "square"
 * (full-bleed, for iOS and maskable icons, which the OS crops itself).
 * `scale` shrinks the drawing around the centre (maskable safe zone).
 */
export function iconSvg(
  variant: IconVariant,
  options: { shape?: 'rounded' | 'square'; size?: number; scale?: number } = {},
): string {
  const { shape = 'rounded', size = 256, scale = 1 } = options
  const offset = (256 * (1 - scale)) / 2
  const body = ICON_SHAPES[variant]
    .map(([tag, attrs]) => {
      const a = Object.entries(attrs)
        .map(([k, v]) => ` ${k}="${v}"`)
        .join('')
      return `<${tag}${a}/>`
    })
    .join('')
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" width="${size}" height="${size}">` +
    `<rect width="256" height="256"${shape === 'rounded' ? ' rx="56"' : ''} fill="${BRAND_INDIGO}"/>` +
    `<g transform="translate(${offset} ${offset}) scale(${scale})">${body}</g></svg>`
  )
}
